import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {ClientConfiguration} from "../configuration/client.config";
import {
    ISelection,
    IExportGlossary,
    ITranslation,
    IGlossaryEntry,
    IText,
    ISentence,
    IWord, IQuestionEntry, ISyllables, IDefinition, IContext, IFact, IQuestion, IAnswer, IWordAudioEntry, IMetaData, IPoetryLevel
} from "../../api/data-model";
import {CssWord} from "../../content/content-view.objects";
import {VocabularyEntry} from "./vocabulary.pipeline.service";
import {AudioQueueService} from "../audio-playback/audioqueue.service";
import {EventBus} from "../../event/EventBus";
import {AudioQueueEvent, InterruptAudio} from "../../event/AudioQueueEvent";
import {AudioQueueItem, TranslateAudio} from "../audio-playback/audioqueue.item";
import {share} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {AudioState, Scilearn} from "../../constants";
import {WordAudioPipelineService} from "./word-audio.pipeline.service";

export enum VerificationAudioCategory {
    SELECTION = "Selection",
    GLOSSARY = "Glossary",
    WORD_AUDIO = "WordAudio"
}

interface IAudioData {
    [file: string]: Set<IAudioDetail>;
}

type Container = IGlossaryEntry | ISyllables | ITranslation | IDefinition | IContext | IFact |
    IQuestion | IAnswer | ISentence | IWordAudioEntry | IMetaData;

interface IAudioDetail {
    selection: string;
    detail: string;
    // reference to the object in the selection data which contains this audio, for setting audioState
    container: Container;
}



@Injectable()
export class VerifierPipelineService {
    private static success = "OK";
    private glossaryErrors = {};
    private glossaryMismatch: Set<string> = new Set([]);
    private audioResults = {};
    private audioObserver: any;
    private audioData: IAudioData;
    private verificationAudioCategory: VerificationAudioCategory;
    private missingAudioDataOfSelections = {};
    private referenceStory: string = "";


    constructor(public http:HttpClient,
                private audioQueueService:AudioQueueService,
                private config:ClientConfiguration,
                private eventBus:EventBus) {
    }

    public resetGlossaryErrors(): void {
        this.glossaryErrors = {};
        this.glossaryMismatch = new Set([]);
    }

    public analyzeErrorsForWord(glossaryTerm: string, glossary: IExportGlossary, word: CssWord): void {
        let glossaryTermSingular = glossaryTerm.replace(/s$/, "");
        // if glossaryTerm does not match entry.word, a) override glossaryTerm
        // with entry.word, and b) note inexact linkage in a separate object
        let entry = glossary[word.glossary];
        if (entry && glossaryTerm !== entry.word.toLowerCase() && !(entry.word.toLowerCase().endsWith(glossaryTerm))
            && !(entry.word.toLowerCase().endsWith(glossaryTermSingular))) {
            let mismatch = "'" + glossaryTerm +
                           "' in text linked to glossary entry: '" + entry.word + "'";
            glossaryTerm = entry.word.toLowerCase();
            this.glossaryMismatch.add(mismatch);
        }
        if (!this.glossaryErrors[word.glossary]) {
            if (!entry) {
                this.glossaryErrors[word.glossary] = glossaryTerm + ": missing glossary entry\n";
            } else {
                let errors = this.getGlossaryErrors(entry, word);
                if (errors) {
                    this.glossaryErrors[word.glossary] = glossaryTerm + ": missing " + errors + "\n";
                } else {
                    this.glossaryErrors[word.glossary] = glossaryTerm + ": (has all required data)\n";
                }
            }
        }
    }

    public getGlossaryErrors(entry: IGlossaryEntry, word: CssWord): string {
        // check if glossary has required: syllables, definition, context sentence, translation

        let errors = [];

        if (!entry.syllables.text) {
            errors.push("syllables");
        }
        if (!entry.definition.text) {
            errors.push("definition");
        }
        if (!entry.context.text) {
            errors.push("context_sentence");
        }
        let translation: ITranslation = entry.translations.find((t: ITranslation) => {
            // tslint:disable-next-line:no-string-literal
            return ((t.id === word.translations["es"]) && (t.language === "es"));
        });
        if (!translation || !translation.word) {
            errors.push("translation");
        }
        return errors.join(",");
    }

    public glossaryErrorString(): string {
        let errorString = "";
        Object.keys(this.glossaryErrors).forEach((id) => {
            errorString += this.glossaryErrors[id];
        });
        errorString += "\n";
        errorString += Array.from(this.glossaryMismatch).join("\n");
        return errorString;
    }

    // TODO: add intro audio check somewhere
    public playGlossaryAudio(selection: ISelection, glossaryEntries: IExportGlossary,
                             playToEnd: boolean): Observable<string> {
        this.verificationAudioCategory = VerificationAudioCategory.GLOSSARY;
        // collect sentence audio file list (add word later)
        this.audioResults = {};
        this.audioData = {};
        this.referenceStory = selection.metaData.referenceStory;
        let language = "es";
        let usedTrans = this.getActiveTranslations(selection, language);
        console.log(JSON.stringify(usedTrans));
        Object.keys(glossaryEntries).forEach((glossaryId) => {
            console.log("glossary id:" + glossaryId);
            let entry = glossaryEntries[glossaryId];
            let termAudio: string;
            let container;
            if (this.isObjectHavingValidKeys(entry.syllables, "glossarySyllables", "text", "audio")) {
                this.addToAudio(selection.metaData.referenceStory, entry.syllables.audio, entry.syllables, "audio for term: " + entry.word);
            } else if (this.isObjectHavingValidKeys(entry.syllables, "glossarySyllables", "word", "normalAudio")) {
                this.addToAudio(selection.metaData.referenceStory, entry.normalAudio, entry, "audio for term: " + entry.word);
            }
            
            if (this.isObjectHavingValidKeys(entry.definition, "glossaryDefinition", "text", "audio")) {
                this.addToAudio(selection.metaData.referenceStory, entry.definition.audio,
                    entry.definition, "definition: " + entry.definition.text + "" +
                    "for term: " + entry.word);
            }
            else {
                console.log("WARNING: term missing definition: " + entry.word);
            }
            // get translations used in story
            let activeTranslations = entry.translations.filter((trans) => usedTrans[glossaryId].indexOf(trans.id) > -1);
            activeTranslations.forEach((trans) => {
                if (this.isObjectHavingValidKeys(trans, "glossaryTranslation", "word", "audio")) {
                    this.addToAudio(selection.metaData.referenceStory, trans.audio, trans,
                        "translation: " + trans.word + " for term: " + entry.word);
                }
            });
            if (this.isObjectHavingValidKeys(entry.context, "glossaryContext", "text", "audio")) {
                this.addToAudio(selection.metaData.referenceStory, entry.context.audio, entry.context,
                    "context sentence: " + entry.context.text + " for term: " + entry.word);
            }
            if (this.isObjectHavingValidKeys(entry.fact, "glossaryFact", "text", "audio")) {
                this.addToAudio(selection.metaData.referenceStory, entry.fact.audio, entry.fact,
                    "fun fact: " + entry.fact.text + " for term: " + entry.word);
            }
        });
        let audioList:string[] = Object.keys(this.audioData);
        this.playNext(audioList, playToEnd);
        return new Observable<string>(observer =>
            this.audioObserver = observer).pipe(share());

    }

    private getActiveTranslations(selection: ISelection, language: string): object {
        let usedTrans = {};
        selection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.words.forEach((word: IWord) => {
                        if (word.glossary) {
                            if (!usedTrans[word.glossary]) {
                                usedTrans[word.glossary] = [word.translations[language]];
                            } else {
                                usedTrans[word.glossary].push(word.translations[language]);
                            }
                        }
                    });
                });
            }
        });
        if (!!selection.vocabularyEntries) {
            selection.vocabularyEntries.forEach((entry: VocabularyEntry) => {
                if (!usedTrans[entry.glossary]) {
                    usedTrans[entry.glossary] = [entry.translations[language]];
                } else {
                    usedTrans[entry.glossary].push(entry.translations[language]);
                }
            });
        }
        return usedTrans;
    }

    public playQuestionAudio(selection: ISelection, playToEnd: boolean): Observable<string> {
        // collect sentence audio file list (add word later)
        this.verificationAudioCategory = VerificationAudioCategory.SELECTION;
        this.referenceStory = selection.metaData.referenceStory;
        this.audioResults = {};
        this.audioData = {};
        selection.questionEntries.forEach((question: IQuestionEntry) => {
            let type = question.presentation === Scilearn.THINK_ABOUT_IT ? "Think About It" : "Quiz";
            if (this.isObjectHavingValidKeys(question.question, "question", "text", "audio")) {
                this.addToAudio(selection.metaData.referenceStory, question.question.audio, question.question,
                    type + ":" + question.question.text);
            }
            if (question.type !== "OpenBook" && question.answers) {
                question.answers.forEach((answer) => {
                    if (this.isObjectHavingValidKeys(answer, "answer", "text", "audio")) {
                        this.addToAudio(selection.metaData.referenceStory, answer.audio, answer,
                            type + " answer:" + answer.text);
                    }
                    if (answer.hints) {
                        answer.hints.forEach((hint) => {
                            if (this.isObjectHavingValidKeys(hint, "answerHint", "text", "audio")) {
                                this.addToAudio(selection.metaData.referenceStory, hint.audio, hint,
                                    type + " hint:" + hint.text);
                            }
                        });
                    }
                });
            }
            if (question.hints) {
                question.hints.forEach((hint) => {
                    if (this.isObjectHavingValidKeys(hint, "questionHint", "text", "audio")) {
                        this.addToAudio(selection.metaData.referenceStory, hint.audio, hint,
                            type + " hint:" + hint.text);
                    }
                });
            }

        });
        // start playing process.  return observable on pattern of recognizer.
        // when reach end of list. call this.audioObserver.next(errorString);
        let audioList:string[] = Object.keys(this.audioData);
        this.playNext(audioList, playToEnd);
        return new Observable<string>(observer =>
            this.audioObserver = observer).pipe(share());

    }

    public playReadToMeAudio(selection: ISelection, playToEnd: boolean): Observable<string> {
        this.verificationAudioCategory = VerificationAudioCategory.SELECTION;
        this.referenceStory = selection.metaData.referenceStory;
        // collect sentence audio file list (add word later)
        this.audioResults = {};
        this.audioData = {};

        selection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    if (this.isObjectHavingValidKeys(sentence, "sentence", "", "audio", true)) {
                        this.addToAudio(selection.metaData.referenceStory, sentence.audio, sentence, "sentence " + sentence.id);
                    }
                });
            }
        });
        // start playing process.  return observable on pattern of recognizer.
        // when reach end of list. call this.audioObserver.next(errorString);
        let audioList:string[] = Object.keys(this.audioData);
        this.playNext(audioList, playToEnd);
        return new Observable<string>(observer =>
            this.audioObserver = observer).pipe(share());

    }

    public playIntoAndTitleAudio(selection: ISelection, playToEnd: boolean): Observable<string> {
        this.verificationAudioCategory = VerificationAudioCategory.SELECTION;
        this.referenceStory = selection.metaData.referenceStory;
        // collect sentence audio file list (add word later)
        this.audioResults = {};
        this.audioData = {};
        if (this.isObjectHavingValidKeys(selection.metaData, "metadata", "", "titleAudio", true)) {
            this.addToAudio(selection.metaData.referenceStory, selection.metaData.titleAudio, selection.metaData, "TitleAudio " + this.referenceStory);
        }
        if (this.isObjectHavingValidKeys(selection.metaData, "metadata", "", "introAudio", true)) {
            this.addToAudio(selection.metaData.referenceStory, selection.metaData.introAudio, selection.metaData, "IntroAudio " + this.referenceStory);
        }

        // start playing process.  return observable on pattern of recognizer.
        // when reach end of list. call this.audioObserver.next(errorString);
        let audioList:string[] = Object.keys(this.audioData);
        this.playNext(audioList, playToEnd);
        return new Observable<string>(observer =>
            this.audioObserver = observer).pipe(share());

    }

    public playWordAudio(selection: ISelection, wordAudioService: WordAudioPipelineService,
                         playToEnd: boolean): Observable<string> {
        this.verificationAudioCategory = VerificationAudioCategory.WORD_AUDIO;
        this.referenceStory = selection.metaData.referenceStory;
        // collect sentence audio file list (add word later)
        this.audioResults = {};
        this.audioData = {};
        selection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.words.forEach((word: IWord) => {
                        let wordInfo = word.word;
                        if (word.dictionary && word.dictionary.length) {
                            wordInfo += ":" + word.dictionary.join(",");
                        }
                        if (this.isObjectHavingValidKeys(word, "word", "word", "audio")) {
                            this.addToAudio(selection.metaData.referenceStory, word.audio, wordAudioService.getWord(word), wordInfo);
                        }
                    });
                });
            }
        });
        // start playing process.  return observable on pattern of recognizer.
        // when reach end of list. call this.audioObserver.next(errorString);
        let audioList:string[] = Object.keys(this.audioData);
        this.playNext(audioList, playToEnd);
        return new Observable<string>(observer =>
            this.audioObserver = observer).pipe(share());

    }


    private isObjectHavingValidKeys(object: any, type: string, keyDependentOn: string, keyToBeCheck: string, skipDependentOn: boolean = false): boolean {
        const isKeyDependentOnValid = (typeof object[keyDependentOn] === 'string' && object[keyDependentOn].length > 0) || skipDependentOn;
        const isAudioValid: boolean = isKeyDependentOnValid && typeof object[keyToBeCheck] === 'string' && object[keyToBeCheck].length > 0;

        if (!isAudioValid && isKeyDependentOnValid) {
            if (!this.missingAudioDataOfSelections[this.referenceStory]) {
                this.missingAudioDataOfSelections[this.referenceStory] = {};
            }
            if (!!this.missingAudioDataOfSelections[this.referenceStory][type]) {
                this.missingAudioDataOfSelections[this.referenceStory][type].push(object);
            } else {
                this.missingAudioDataOfSelections[this.referenceStory][type] = [object];
            }
        }
        return isAudioValid;
    }


    private addToAudio(selectionTitle: string, file: string, container: Container, detail: string): void {
        if (!file) {
            file = "_AUDIO_FILE_MISSING";
        }
        if (!this.audioData[file]) {
            this.audioData[file] = new Set<IAudioDetail>();
        }
        this.audioData[file].add({selection: selectionTitle, container, detail});
    }

    private playNext(audioList: string[], playToEnd: boolean): void {
        // start playing process.
        // when reach end of list. call this.audioObserver.next(errorString);
        const file = audioList[0];
        this.eventBus.next(new AudioQueueEvent(
            [new AudioQueueItem
             (this.config.audioUrl + file, TranslateAudio.NO,
                                                   () => {
                                                       this.processResult(VerifierPipelineService.success, file);
                                                       this.continuePlaying(audioList, playToEnd);
                                                   },
                                                   (e) => {
                                                       this.processResult(e, file);
                                                       this.continuePlaying(audioList, playToEnd);
                                                   },
                                                   () => {
                                                       // is playing callback (if we get this far,
                                                       // know we don't have corruption/404)
                                                       if (!playToEnd) {
                                                           this.processResult(VerifierPipelineService.success, file);
                                                           this.audioQueueService.hardReset();
                                                           this.continuePlaying(audioList, playToEnd);
                                                       }
                                                   })], InterruptAudio.NO));
    }

    private continuePlaying(audioList: string[], playToEnd: boolean): void {
        let audioItem = audioList.shift();
        console.log("processed file: " + audioItem);
        if (audioList.length) {
            this.playNext(audioList, playToEnd);
        } else {
            const errorList = Object.keys(this.audioResults).filter(key => this.audioResults[key] !== VerifierPipelineService.success);
            let errors = errorList.map(e => e + ": " + this.audioResults[e]).join("\n");
            // let errors = JSON.stringify(this.audioResults, null, 2);
            if (errorList.length === 0) {
                errors = "No errors found.";
            }
            this.audioObserver.next(errors);
        }
    }

    public transitionAudioState() {
        // TODO: set the audio state to missing where appropriate. not really distinguishing between 404s and bad data here
        for (let file in this.audioResults) {
            if (this.audioResults.hasOwnProperty(file)) {
                const audioState = this.audioResults[file] === VerifierPipelineService.success ? AudioState.UPLOAD_CONFIRMED :
                    AudioState.MISSING;
                const contentDetails = this.audioData[file];
                console.log(contentDetails);
                for (let detail of contentDetails) {
                    detail.container.audioState = audioState;
                    console.log(detail.container);
                }
            }
        }

    }

    // TODO set audioState in corresponding object.  or - do we want a check/alert here? Separate step?
    private processResult(result, file): void {
        console.log(result, file);
        if (result === VerifierPipelineService.success) {
            this.audioResults[file] = result;
        }
        else if (result) {
            let info = result.split(":");
            let audioFilename = info[1].replace(/.*[/](.+)/, "$1");
            console.log("info[1]:" + info[1] + "filename: " + audioFilename + " orig: " + file);
            let errorMessage = info[2] + " " + info[3];
            let audioInformation = " selection(s):" +
                                   Array.from(this.audioData[audioFilename]).map((a) => {
                                       return a.selection + "-" + a.detail;
                                   }).join(",");
            console.log("found audio error: " + errorMessage + audioInformation);
            this.audioResults[audioFilename] = errorMessage + audioInformation;
        } else {
            let category = "unknown";
            if (!this.audioResults[category]) {
                this.audioResults[category] = "";
            }
            this.audioResults[category] += "no information found for error, check network tab\n";
        }

    }

    public checkSelectionMetaData(selection: ISelection): boolean {
        const { poetryLevel, fountasPinnellLevel, ATOSGradeEquivalent, lexileLevel } = selection.metaData;

        if (this.isNullOrEmpty(fountasPinnellLevel) || this.isNullOrEmpty(ATOSGradeEquivalent) || this.isNullOrEmpty(lexileLevel)) {
            return false;
        }
        if (ATOSGradeEquivalent !== 'NP' && this.isNumberInRange(ATOSGradeEquivalent, 0.0, 16.0)) {
            const isValidLexileAndPinnelLevel = lexileLevel !== 'NP' && this.isInteger(lexileLevel) && fountasPinnellLevel !== 'NP' && this.isCapitalLetter(fountasPinnellLevel);
            if(!isValidLexileAndPinnelLevel){
                return false;
            }
        } else if (ATOSGradeEquivalent === 'NP') {
            const isValidLexileAndPinnelLevel = (lexileLevel === 'NP' || this.isInteger(lexileLevel)) && (fountasPinnellLevel === 'NP' || this.isCapitalLetter(fountasPinnellLevel));
            if(!isValidLexileAndPinnelLevel || !this.isValidPoetryLevel(poetryLevel)){
                return false;
            }
        } else {
            return false;
        }
        return true;
    }

    public isValidPoetryLevel(poetryLevel: IPoetryLevel): boolean {
        if (!poetryLevel) {
          return false;
        }
      
        const { fountasPinnellLevel, ATOSGradeEquivalent, lexileLevel } = poetryLevel;
        return (
          !this.isNullOrEmpty(fountasPinnellLevel) &&
          !this.isNullOrEmpty(ATOSGradeEquivalent) &&
          !this.isNullOrEmpty(lexileLevel) &&
          typeof fountasPinnellLevel === 'string' &&
          typeof ATOSGradeEquivalent === 'string' &&
          typeof lexileLevel === 'string' &&
          this.isNumberInRange(ATOSGradeEquivalent, 0.0, 16.0) &&
          this.isCapitalLetter(fountasPinnellLevel) &&
          this.isInteger(lexileLevel)
        );
      }

    public isNullOrEmpty(str: string | undefined | null): boolean {
        return !str || str.trim().length === 0;
    }

    public isNumberInRange(str: string, min: number, max: number): boolean {
        console.log(str);
        const value = parseFloat(str);
        return !isNaN(value) && value >= min && value <= max;
    }

    public isCapitalLetter(str: string): boolean {
        return str.match(/^[A-Z]$/) !== null;
    }

    public isInteger(str: string): boolean {
        return str.match(/^\d+$/) !== null;
    }

    public async checkSelectionImageData(selection: ISelection): Promise<any[]> {
        let contentToVerify = [];

        // selection Images
        selection.text.forEach((text: IText) => {
            if (!!text.mediaFormat) {
                const contentSource = { type: "SelectionSource", source: this.config.selectionImageUrl(text.mediaFormat.source) };
                contentToVerify.push(contentSource);
            }
        });

        // glossary Images
        if (!!selection.glossaryEntries) {
            const glossaryKeys = Object.keys(selection.glossaryEntries);
            glossaryKeys.forEach(key => {
                if (!!selection.glossaryEntries[key].context.media) {
                    const glossarySource = { type: "GlossarySource", source: this.config.selectionImageUrl(selection.glossaryEntries[key].context.media) };
                    contentToVerify.push(glossarySource);
                }
            });
        }

        // cover Images
        if (!!selection.metaData && !!selection.metaData.coverImage) {
            let imageName = selection.metaData.coverImage;
            let imageUrl = this.config.selectionImageUrl(selection.metaData.coverImage);
            contentToVerify.push({ type: "CoverImage", source: imageUrl.replace(imageName, `library/${imageName}`) });
            contentToVerify.push({ type: "CoverImage", source: imageUrl.replace(imageName, `points/${imageName}`) });
            contentToVerify.push({ type: "CoverImage", source: imageUrl.replace(imageName, `intro/${imageName}`) });
        }

        let imagesNotPresent = [];
        for (let i = 0; i < contentToVerify.length; i++) {
            try {
                const response = await fetch(contentToVerify[i].source);
                if (response.status === 200) {
                    console.log(`URL: ${contentToVerify[i].source} valid`);
                } else {
                    imagesNotPresent.push(contentToVerify[i]);
                }
            } catch (e) {
                imagesNotPresent.push(contentToVerify[i]);
            }
        }
        return imagesNotPresent
    }

    public resetMissingAudioDataOfSelections():void {
        this.missingAudioDataOfSelections = {}
    }

    get audioCategory() {
        return this.verificationAudioCategory;
    }

    get missingAudioData() {
        return this.missingAudioDataOfSelections;
    }

}
